import type {
  AcademyOperatingProfileInput,
  OnboardingGoal,
  OnboardingWorkflow,
} from '../contracts/academyOnboarding';
import { ONBOARDING_WORKFLOWS } from '../contracts/academyOnboarding';

const GOAL_TO_WORKFLOWS: Record<OnboardingGoal, readonly OnboardingWorkflow[]> = {
  reduce_admin: ['waitlists', 'makeup_credits', 'coach_attendance'],
  grow_enrollment: ['group_programs', 'camps', 'student_portals', 'student_self_booking'],
  simplify_scheduling: ['group_programs', 'private_lessons'],
  improve_communication: ['student_portals'],
  manage_payments: ['online_payments'],
  player_development: ['programs_pathways', 'group_programs', 'private_lessons'],
};

export type AcademySetupTaskId =
  | 'academy_profile'
  | 'sports'
  | 'courts'
  | 'locations'
  | 'coaches'
  | 'terms'
  | 'lessons'
  | 'programs'
  | 'payments';

export interface AcademySetupTaskLike {
  id: AcademySetupTaskId;
  title: string;
  description: string;
  href: string;
  done: boolean;
}

export interface PersonalizeSetupTaskCopyOptions {
  hasPaymentProcessingFeature?: boolean;
}

export type PostSubscriptionStepId = 'team' | 'programs' | 'payments';

function profileGoals(profile: Partial<AcademyOperatingProfileInput>): OnboardingGoal[] {
  return profile.goals ?? [];
}

function profileWorkflows(profile: Partial<AcademyOperatingProfileInput>): OnboardingWorkflow[] {
  return profile.desiredWorkflows ?? [];
}

function hasGoal(profile: Partial<AcademyOperatingProfileInput>, goal: OnboardingGoal): boolean {
  return profileGoals(profile).includes(goal);
}

function hasWorkflow(profile: Partial<AcademyOperatingProfileInput>, workflow: OnboardingWorkflow): boolean {
  return profileWorkflows(profile).includes(workflow);
}

function hasAnyWorkflow(
  profile: Partial<AcademyOperatingProfileInput>,
  workflows: readonly OnboardingWorkflow[]
): boolean {
  return workflows.some((workflow) => hasWorkflow(profile, workflow));
}

/** Legacy goal or matching workflow selection. */
function wantsGrowEnrollment(profile: Partial<AcademyOperatingProfileInput>): boolean {
  return (
    hasGoal(profile, 'grow_enrollment') ||
    hasAnyWorkflow(profile, ['student_portals', 'student_self_booking', 'group_programs', 'camps'])
  );
}

function wantsOnlinePayments(profile: Partial<AcademyOperatingProfileInput>): boolean {
  return hasGoal(profile, 'manage_payments') || hasWorkflow(profile, 'online_payments');
}

function wantsSimplifyScheduling(profile: Partial<AcademyOperatingProfileInput>): boolean {
  return (
    hasGoal(profile, 'simplify_scheduling') ||
    hasAnyWorkflow(profile, ['group_programs', 'private_lessons'])
  );
}

function wantsReduceAdmin(profile: Partial<AcademyOperatingProfileInput>): boolean {
  return (
    hasGoal(profile, 'reduce_admin') ||
    hasAnyWorkflow(profile, ['waitlists', 'makeup_credits', 'coach_attendance'])
  );
}

function wantsImproveCommunication(profile: Partial<AcademyOperatingProfileInput>): boolean {
  return hasGoal(profile, 'improve_communication') || hasWorkflow(profile, 'student_portals');
}

function wantsPlayerDevelopment(profile: Partial<AcademyOperatingProfileInput>): boolean {
  return hasGoal(profile, 'player_development') || hasWorkflow(profile, 'programs_pathways');
}

export function wantsCampsWorkflow(profile: Partial<AcademyOperatingProfileInput>): boolean {
  return hasWorkflow(profile, 'camps');
}

export function wantsGroupProgramsWorkflow(profile: Partial<AcademyOperatingProfileInput>): boolean {
  return hasWorkflow(profile, 'group_programs');
}

export function wantsPrivateLessonsWorkflow(profile: Partial<AcademyOperatingProfileInput>): boolean {
  return hasWorkflow(profile, 'private_lessons');
}

/** Camps selected without term-based group programs — holiday camps, not term lessons. */
export function isCampsOnlyScheduling(
  profile: Partial<AcademyOperatingProfileInput> | null | undefined
): boolean {
  if (!profile) return false;
  return wantsCampsWorkflow(profile) && !wantsGroupProgramsWorkflow(profile);
}

/** Private lessons without term-based group programs or camps — standalone packages on Calendar. */
export function isPrivateLessonsOnlyScheduling(
  profile: Partial<AcademyOperatingProfileInput> | null | undefined
): boolean {
  if (!profile) return false;
  if (!wantsPrivateLessonsWorkflow(profile)) return false;
  return !wantsGroupProgramsWorkflow(profile) && !wantsCampsWorkflow(profile);
}

/** Solo coaches without group/camp workflows typically run private lesson packages. */
export function isSoloCoachPrivateLessonsProfile(
  profile: Partial<AcademyOperatingProfileInput> | null | undefined
): boolean {
  if (!profile) return false;
  if (profile.operatingRole !== 'solo_coach') return false;
  return !wantsGroupProgramsWorkflow(profile) && !wantsCampsWorkflow(profile);
}

export function shouldPersonalizePrivateLessonsSetup(
  profile: Partial<AcademyOperatingProfileInput> | null | undefined
): boolean {
  if (!profile) return false;
  return isPrivateLessonsOnlyScheduling(profile) || isSoloCoachPrivateLessonsProfile(profile);
}

export function shouldShowTermsSetupTask(
  profile: Partial<AcademyOperatingProfileInput> | null | undefined
): boolean {
  if (!profile) return true;
  if (isCampsOnlyScheduling(profile)) return false;
  if (shouldPersonalizePrivateLessonsSetup(profile)) return false;
  return true;
}

export function resolveLessonsSetupTaskHref(
  profile: Partial<AcademyOperatingProfileInput> | null | undefined
): string {
  if (!profile) return '/admin/calendar';
  if (isCampsOnlyScheduling(profile)) return '/admin/camps';
  if (wantsGroupProgramsWorkflow(profile)) return '/admin/terms';
  if (shouldPersonalizePrivateLessonsSetup(profile)) return '/admin/calendar?create=private';
  return '/admin/calendar';
}

export type AcademySetupPageTourKey =
  | 'organization-settings'
  | 'academy-lessons'
  | 'academy-payments';

const SETUP_TASK_TOUR_KEY_MAP: Partial<Record<AcademySetupTaskId, AcademySetupPageTourKey>> = {
  academy_profile: 'organization-settings',
  sports: 'organization-settings',
  lessons: 'academy-lessons',
  payments: 'academy-payments',
};

/** Page tour for the lessons checklist task; matches resolveLessonsSetupTaskHref destination. */
export function resolveLessonsSetupTaskTourKey(
  profile: Partial<AcademyOperatingProfileInput> | null | undefined
): AcademySetupPageTourKey | null {
  const href = resolveLessonsSetupTaskHref(profile);
  if (href.startsWith('/admin/camps') || href.startsWith('/admin/terms')) {
    return null;
  }
  return 'academy-lessons';
}

/** Maps a checklist task to its page tour key. Lessons follows resolveLessonsSetupTaskHref. */
export function resolveSetupTaskTourKey(
  taskId: AcademySetupTaskId,
  profile: Partial<AcademyOperatingProfileInput> | null | undefined
): AcademySetupPageTourKey | null {
  if (taskId === 'lessons') {
    return resolveLessonsSetupTaskTourKey(profile);
  }
  return SETUP_TASK_TOUR_KEY_MAP[taskId] ?? null;
}

/** Workflows implied by selected goals, before profile-aware filtering. */
export function suggestWorkflowsFromGoals(
  profile: Partial<AcademyOperatingProfileInput>
): OnboardingWorkflow[] {
  const suggested = new Set<OnboardingWorkflow>();
  for (const goal of profileGoals(profile)) {
    for (const workflow of GOAL_TO_WORKFLOWS[goal] ?? []) {
      suggested.add(workflow);
    }
  }
  return filterWorkflowsForProfile(profile, [...suggested]);
}

function filterWorkflowsForProfile(
  profile: Partial<AcademyOperatingProfileInput>,
  workflows: OnboardingWorkflow[]
): OnboardingWorkflow[] {
  return workflows.filter((workflow) => {
    if (workflow === 'coach_portals' && profile.operatingRole === 'solo_coach') {
      return false;
    }
    if (
      workflow === 'multi_location' &&
      profile.locationCountBand === '1' &&
      !hasWorkflow(profile, 'multi_location')
    ) {
      return false;
    }
    return true;
  });
}

/** Workflows available on the questionnaire step for this profile. */
export function workflowOptionsForOperatingProfile(
  profile: Partial<AcademyOperatingProfileInput>
): OnboardingWorkflow[] {
  const withoutCoachPortals = ONBOARDING_WORKFLOWS.filter(
    (workflow) => workflow !== 'coach_portals'
  ) as OnboardingWorkflow[];

  const filtered = filterWorkflowsForProfile(profile, [...withoutCoachPortals]);

  if (hasWorkflow(profile, 'multi_location') && !filtered.includes('multi_location')) {
    filtered.push('multi_location');
  }

  return filtered;
}

/** Additive merge of goal-implied workflows into desiredWorkflows. */
export function mergeSuggestedWorkflows(
  profile: Partial<AcademyOperatingProfileInput>
): Partial<AcademyOperatingProfileInput> {
  const existing = profile.desiredWorkflows ?? [];
  const suggested = suggestWorkflowsFromGoals(profile);
  const merged = [...existing];
  for (const workflow of suggested) {
    if (!merged.includes(workflow)) merged.push(workflow);
  }
  return { ...profile, desiredWorkflows: merged };
}

/** Goal-implied workflows for UI badges (includes already-selected suggestions). */
export function getGoalSuggestedWorkflows(
  profile: Partial<AcademyOperatingProfileInput>
): OnboardingWorkflow[] {
  if ((profile.goals?.length ?? 0) === 0) return [];
  return suggestWorkflowsFromGoals(profile);
}

export function shouldShowProgramsSetupTask(
  profile: Partial<AcademyOperatingProfileInput> | null | undefined
): boolean {
  if (!profile) return false;
  return wantsPlayerDevelopment(profile);
}

export function getSetupTaskPriority(
  taskId: AcademySetupTaskId,
  profile: Partial<AcademyOperatingProfileInput> | null | undefined
): number {
  if (!profile) return 0;

  let priority = 0;
  const bump = (amount: number) => {
    priority += amount;
  };

  if (taskId === 'academy_profile' && wantsGrowEnrollment(profile)) bump(30);
  if (taskId === 'lessons' && wantsGrowEnrollment(profile)) bump(40);
  if (taskId === 'payments' && wantsOnlinePayments(profile)) bump(35);

  if (taskId === 'courts' && wantsSimplifyScheduling(profile)) bump(35);
  if (taskId === 'terms' && wantsSimplifyScheduling(profile)) bump(30);
  if (taskId === 'lessons' && wantsSimplifyScheduling(profile)) bump(25);

  if (taskId === 'coaches' && shouldPersonalizePrivateLessonsSetup(profile)) bump(30);
  if (taskId === 'lessons' && shouldPersonalizePrivateLessonsSetup(profile)) bump(35);
  if (taskId === 'courts' && shouldPersonalizePrivateLessonsSetup(profile)) bump(20);

  if (taskId === 'programs' && shouldShowProgramsSetupTask(profile)) bump(40);
  if (taskId === 'programs' && wantsPlayerDevelopment(profile)) bump(20);

  if (taskId === 'locations' && hasWorkflow(profile, 'multi_location')) bump(35);
  if (taskId === 'courts' && hasWorkflow(profile, 'multi_location')) bump(20);
  if (
    (taskId === 'locations' || taskId === 'courts') &&
    (profile.locationCountBand === '2-3' || profile.locationCountBand === '4+')
  ) {
    bump(15);
  }

  if (taskId === 'terms' && wantsReduceAdmin(profile)) bump(15);
  if (taskId === 'lessons' && wantsReduceAdmin(profile)) bump(10);
  if (taskId === 'coaches' && wantsReduceAdmin(profile)) bump(5);

  if (taskId === 'academy_profile' && wantsImproveCommunication(profile)) bump(25);

  return priority;
}

/**
 * Canonical setup order — lessons require courts (and coaches when shown) to exist first.
 * Profile priority may reorder tasks within the same band only.
 */
const SETUP_TASK_PREREQUISITE_RANK: Record<AcademySetupTaskId, number> = {
  academy_profile: 0,
  sports: 10,
  courts: 20,
  locations: 30,
  coaches: 40,
  terms: 50,
  programs: 60,
  lessons: 70,
  payments: 80,
};

function getSetupTaskPrerequisiteRank(taskId: AcademySetupTaskId): number {
  return SETUP_TASK_PREREQUISITE_RANK[taskId];
}

/** Stable sort: prerequisite order first, then profile priority; ties keep original order. */
export function sortSetupTasksByProfile<T extends AcademySetupTaskLike>(
  tasks: T[],
  profile: Partial<AcademyOperatingProfileInput> | null | undefined
): T[] {
  return tasks
    .map((task, index) => ({
      task,
      index,
      prerequisiteRank: getSetupTaskPrerequisiteRank(task.id),
      priority: getSetupTaskPriority(task.id, profile),
    }))
    .sort(
      (a, b) =>
        a.prerequisiteRank - b.prerequisiteRank ||
        b.priority - a.priority ||
        a.index - b.index,
    )
    .map(({ task }) => task);
}

export function personalizeSetupTaskCopy<T extends AcademySetupTaskLike>(
  task: T,
  profile: Partial<AcademyOperatingProfileInput> | null | undefined,
  options: PersonalizeSetupTaskCopyOptions = {}
): T {
  if (!profile || task.done) return task;

  const { hasPaymentProcessingFeature = false } = options;

  if (task.id === 'lessons' && isCampsOnlyScheduling(profile)) {
    return {
      ...task,
      title: 'Create camps',
      description: 'Set up camp dates and pricing so families can discover and book your programs.',
    };
  }

  if (task.id === 'lessons' && shouldPersonalizePrivateLessonsSetup(profile)) {
    const starterDescription =
      'Open Calendar → New lesson → Private. Set session dates, coach, and package price, then enroll students from Bookings.';
    const proDescription =
      'Open Calendar → New lesson → Private. Set session dates, coach, and pricing — then enroll students or share the booking link.';
    return {
      ...task,
      title: 'Set up private lessons',
      description: hasPaymentProcessingFeature ? proDescription : starterDescription,
    };
  }

  if (task.id === 'coaches' && shouldPersonalizePrivateLessonsSetup(profile)) {
    return {
      ...task,
      description: profile.coachesNeedPortalAccess
        ? 'Add coaches with hourly rates — required before you can schedule private lessons.'
        : 'Add yourself or other coaches with hourly rates — required before scheduling private lessons.',
    };
  }

  if (task.id === 'lessons' && wantsGrowEnrollment(profile)) {
    return {
      ...task,
      description: 'Publish lessons so families can discover and book your programs.',
    };
  }

  if (task.id === 'programs' && wantsPlayerDevelopment(profile)) {
    return {
      ...task,
      description:
        'Create development pathways with target skills so coaches can track player progress.',
    };
  }

  if (task.id === 'payments' && wantsOnlinePayments(profile)) {
    return {
      ...task,
      description: hasPaymentProcessingFeature
        ? 'Connect Stripe so families can pay when they book lessons online.'
        : 'Add clear payment instructions families see when they request or confirm bookings.',
    };
  }

  if (task.id === 'academy_profile' && wantsImproveCommunication(profile)) {
    return {
      ...task,
      description:
        'Add contact details and review notification settings so families hear from you reliably.',
    };
  }

  if (task.id === 'courts' && wantsSimplifyScheduling(profile)) {
    return {
      ...task,
      description: 'Set up courts first so you can schedule lessons without conflicts.',
    };
  }

  return task;
}

export function getPostSubscriptionStepCopy(
  stepId: PostSubscriptionStepId,
  profile: Partial<AcademyOperatingProfileInput> | null | undefined
): string | undefined {
  if (!profile) return undefined;

  if (stepId === 'team') {
    if (wantsGrowEnrollment(profile) && profile.operatingRole === 'owner_coach') {
      return 'Invite coaches so you can scale programs while families self-book online.';
    }
    if (wantsReduceAdmin(profile)) {
      return 'Add coaches and give them portal access so they can manage their own schedules.';
    }
  }

  if (stepId === 'programs') {
    if (wantsPlayerDevelopment(profile)) {
      return 'Set up development pathways when you are ready — link lessons to skills and goals.';
    }
    if (shouldPersonalizePrivateLessonsSetup(profile)) {
      return 'On Calendar, create a private lesson package with session dates and pricing. Starter plans enroll students from Bookings; Professional+ can also enable public private-lesson requests.';
    }
    if (wantsGrowEnrollment(profile)) {
      return 'Create terms and lessons when you are ready. On Starter, families browse your public catalogue and submit booking requests.';
    }
  }

  if (stepId === 'payments') {
    if (wantsOnlinePayments(profile)) {
      return 'Connect Stripe so families can pay when they book — you can finish this anytime.';
    }
    if (wantsReduceAdmin(profile)) {
      return 'Set up Stripe Connect or add manual payment instructions to cut down on payment chasing.';
    }
  }

  return undefined;
}
